import { AnyRoute, interpolatePath } from '@tanstack/react-router';
import { array, option, record } from 'fp-ts';
import { constVoid, pipe } from 'fp-ts/function';
import { PLATFORM_ADMIN_ENTERPRISE_ID } from '@mews-commander/constants';
import { getCurrentEnterpriseId, getStrictCurrentEnterpriseId, updateCurrentSession } from '@mews-commander/core';
import { fetchGlobalization } from '@mews-commander/core/legacy-globalization';
import {
    Configuration,
    configurationSchema,
    createGraphqlClientConfigContextProvider,
    createRepositoryRegistry,
    createRepositoryRegistryProvider,
    ensureRepositoryQueryDataFactory,
    getAppConfiguration,
    setAppConfiguration,
} from '@mews-commander/data-access';
import { FeatureFlag, RouteName } from '@mews-commander/enums';
import { setAppInitChain, setAppInstance } from '@mews-commander/legacy-app-instance';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    addCustomValidators,
    commanderContextProviderFactory,
    configureStore,
    createContainer,
    createCultureFormatValidators,
    createLocalizationHandlers,
    createLocalizationProvider,
    createReactQueryClient,
    DEFAULT_SERVER_BASE_PATH,
    fetchCultureInfos,
    fetchLocalizedTexts,
    GAINSIGHT_ADA_SWITCH_FUNCTION,
    getCommonParams,
    getCurrentDateTimeLocalizationInfo,
    initApp,
    IntlLocalizationProvider,
    loadIconFont,
    localFeatureFlags,
    navigationOccurred,
    prepareServicesRequiringConsents,
    prepareTrackingConsents,
    renderApp,
    reportNewRelicEvents,
    reportNewRelicPerformanceObserverEvents,
    scheduleAppCleanup,
    setNavigatedBack,
    setNewRelicCustomAttributes,
    SUPPORTED_PROTOCOLS_REGEX,
    teardown,
    tryAutomaticSignIn,
} from '@mews-commander/modules';
import { ThunkContext } from '@mews-commander/types';
import { RoutePaths, setShouldRestoreScroll } from '@mews-commander/ui';
import { createUrlFromPath } from '@mews-commander/utils';
import { PlatformEnvironment } from '@mews/enums';
import {
    AppError,
    createAppClockService,
    createAppClockServiceProvider,
    createCalendarService,
    createCaptchaService,
    createFeatureFlagService,
    createFeatureFlagServiceProvider,
    createFullviewService,
    createGainsightService,
    createLoggerServiceProvider,
    createMemoryMonitoringService,
    createPciProxyService,
    createPerformanceMonitoringService,
    createTimeService,
    createTrackingConsentService,
    getNewRelicMemoryEvent,
    initializeBrowserFingerprint,
    logSentryError,
    observePerformance,
    PerformanceMonitoringService,
    setSentryTags,
} from '@mews/framework';
import { createRouteResolveProvider, createRoutingHistoryMiddleware } from '@mews/routing';
import { getFeatureFlagsFromUrl, isAppVisible } from '@mews/std';
import { checkHostname } from './checkHostname';
import { createSentryServices } from './sentry';
import { createLoggerService, createNewRelicReporterService } from './services';

let isRunning = false;
export default async function start(appConfiguration: Configuration, onInitCompleted = constVoid) {
    const contextWindow = window;

    const { protocol } = contextWindow.location;
    if (!SUPPORTED_PROTOCOLS_REGEX.test(protocol)) {
        // prevent app start when running under 'file://' or other non-web protocols
        return;
    }

    if (isRunning) {
        throw new AppError('Commander application has already started.');
    }
    isRunning = true;

    let performanceMonitoringService: PerformanceMonitoringService | undefined;
    try {
        performanceMonitoringService = createPerformanceMonitoringService();
    } catch (e) {
        contextWindow.console.warn(e.message);
    }

    if (performanceMonitoringService) {
        performanceMonitoringService.markAppStarted();
    }

    const baseUrl = appConfiguration.configuration.routerBaseUrl;
    appConfiguration.configuration.serverBasePath =
        appConfiguration.configuration.serverBasePath || DEFAULT_SERVER_BASE_PATH;

    const { environment } = appConfiguration.configuration;

    // feature flags
    const { launchDarklyToken, launchDarklyOverrides } = appConfiguration.configuration;
    const featureFlagService = createFeatureFlagService({
        apiKey: launchDarklyToken as string,
        configFlagOverrides: launchDarklyOverrides,
        flagOverrides: {
            ...(__LOCALHOST__ === true ? localFeatureFlags : {}),
            ...(environment === PlatformEnvironment.Production ? {} : getFeatureFlagsFromUrl()),
        },
    });

    /**
     * We need chunk here to avoid circular dependency
     */
    const tanStackRouter = await import(
        /* webpackChunkName: "tanStackRouter", webpackMode: "lazy-once", webpackPreload: true, webpackFetchPriority: "high" */
        '@mews-commander/routes'
    ).then((module) => module.createTanStackRouter(baseUrl));

    const RouteNamePathMap: Partial<Record<RouteName, RoutePaths>> = pipe(
        tanStackRouter.routesByPath as Record<RoutePaths, AnyRoute>,
        record.filterMap((route) => option.fromNullable(route.options.staticData.routeName)),
        Object.entries,
        array.map((entry) => entry.reverse()),
        Object.fromEntries
    );

    const [, flagServicePromise] = await Promise.allSettled([
        initializeBrowserFingerprint(),
        /**
         * FeatureFlagService is needed to be initialized as early as possible.
         *
         * The await here will delay a bit the startup. This is a temporary solution however, in a long run all
         * the services here should be created in their own promises and initialized in parallel,
         * not sequentially as they do now, so it's a step in a proper direction.
         */
        featureFlagService.initialize(),
    ]);

    // sentry
    const {
        adminSentryService,
        connectivitySentryService,
        guestPortalSentryService,
        productivitySentryService,
        reservationsSentryService,
        sentryMiddleware,
    } = createSentryServices(appConfiguration.configuration, featureFlagService);

    const appClockService = createAppClockService({
        context: contextWindow,
        getServerNowUtc: () => getAppConfiguration().nowUtc,
    });

    const { applicationInsightsInstrumentationKey, newRelic } = appConfiguration.configuration;
    const loggerService = createLoggerService({
        appClockService,
        applicationInsightsInstrumentationKey,
        environment,
        newRelicLoggerConfig: newRelic,
        sentryReporterService: reservationsSentryService,
    });

    const newRelicReporterService = createNewRelicReporterService({
        appClockService,
        newRelicReporterConfig: newRelic,
        sentryReporterService: productivitySentryService,
    });

    const trackingConsentsService = createTrackingConsentService({ context: contextWindow });

    const { fullViewToken } = appConfiguration.configuration;
    const fullviewService = createFullviewService({
        apiKey: fullViewToken,
        loggerService,
    });

    const { gainsightToken } = appConfiguration.configuration;
    const gainsightService = createGainsightService(contextWindow, gainsightToken, {
        loggerService,
        kcAllowedFuncNames: [GAINSIGHT_ADA_SWITCH_FUNCTION],
    });

    const { pciProxySecureFieldsScriptUrl } = appConfiguration.configuration;
    const pciProxyService = createPciProxyService({ scriptUrl: pciProxySecureFieldsScriptUrl });

    const repositories = createRepositoryRegistry(() => getCommonParams());

    const resolveRoute = (routeName: string, params?: Record<string, string>) => {
        if (!(routeName in RouteNamePathMap)) {
            throw new AppError(`RouteName "${routeName}" not found in router, use tanstack navigation instead.`);
        }

        /**
         * We provide current enterpriseId as a default value to all route resolution.
         * This way, we don't need to provide it as a required parameter.
         */
        const currentEnterpriseId =
            params?.currentEnterpriseId ??
            getStrictCurrentEnterpriseId() ??
            /**
             * In some cases when the currentEnterpriseId is unknown yet, we need to provide some string value to router instead.
             * The route resolution checks correctly for the id to be in GUID format, but throws an error if no value is provided.
             *
             * There is probably a better solution to this, but it requires a deeper inspection and refactoring of the whole
             * routing logic.
             */
            'EMPTY';

        const finalParams = { currentEnterpriseId, ...params };

        // @ts-expect-error
        const { query, fragment, ...tsParams } = finalParams;

        /**
         * We use `interpolatePath` and manual construction here, because `buildLocation` was incurring
         * a performance hit by being slower than the old resolveRoute.
         * And we can use `interpolatePath` here because we know exactly what route we are trying to match
         * from RouteName.
         */
        const { basepath } = tanStackRouter;
        const path = interpolatePath({
            path: RouteNamePathMap[routeName as RouteName],
            params: tsParams,
        });
        const search = tanStackRouter.options.stringifySearch(query);
        const href = `${basepath}${path}${search ?? ''}${fragment ? `#${fragment}` : ''}`;
        return href;
    };

    const getRouteInfo = (url: string) => {
        const urlObject = createUrlFromPath(url);
        const matchedRoute = tanStackRouter.matchRoutes(urlObject?.pathname, urlObject?.search).at(-1);
        if (!matchedRoute) {
            return;
        }
        return {
            owner: matchedRoute.staticData.owner,
            routePath: matchedRoute.fullPath,
        };
    };

    const reactQueryClient = createReactQueryClient({
        logError: ({ error, extra }) => reservationsSentryService?.logError({ error, extra }),
    });
    const ensureQueryData = ensureRepositoryQueryDataFactory(reactQueryClient, repositories);

    // store
    let container: CommanderContainer;
    const thunkContent: ThunkContext = {
        resolveRoute,
        loggerService,
        pciProxyService,
        container: () => container,
        appClockService,
        trackingConsentsService,
        featureFlagService,
        newRelicReporterService,
        connectivitySentryService,
        guestPortalSentryService,
        reservationsSentryService,
        productivitySentryService,
        adminSentryService,
        gainsightService,
        repositories,
        reactQueryClient,
        fullviewService,
        navigate: tanStackRouter.navigate,
        buildLocation: tanStackRouter.buildLocation,
        getRouteInfo,
        ensureQueryData,
    };
    const initialState = {
        app: {
            serverMeta: appConfiguration.server,
        },
    };

    const history = {
        push: tanStackRouter.history.push,
        replace: tanStackRouter.history.replace,
        go: tanStackRouter.history.go,
        goForward: () => tanStackRouter.history.forward(),
        goBack: () => tanStackRouter.history.back(),
    };

    const middlewares = [...(sentryMiddleware ? [sentryMiddleware] : []), createRoutingHistoryMiddleware({ history })];

    const store = configureStore({
        initialState,
        middlewares,
        sentryReporterService: reservationsSentryService,
        thunkContent,
    });

    // container
    container = createContainer({
        store,
        contextWindow,
    });

    try {
        configurationSchema.parse(appConfiguration);
    } catch (error) {
        store.dispatch(logSentryError({ error }));
    }

    // prepare app teardown
    contextWindow.addEventListener('beforeunload', () => {
        store.dispatch(teardown());
    });
    contextWindow.addEventListener('visibilitychange', () => {
        if (!isAppVisible()) {
            newRelicReporterService.flushEventsQueue();
        }
    });
    // validators
    addCustomValidators(createCultureFormatValidators(store));

    observePerformance({
        context: window,
        entryTypes: ['longtask', 'resource'],
        callback: (entryList) => {
            store.dispatch(reportNewRelicPerformanceObserverEvents(entryList, { context: contextWindow }));
        },
    });

    createMemoryMonitoringService({
        context: contextWindow,
        report: (event) => {
            const newRelicEvent = getNewRelicMemoryEvent(event);
            store.dispatch(reportNewRelicEvents([newRelicEvent]));
        },
    });

    const sitekey = appConfiguration.configuration.cloudflareCaptchaSiteKey ?? '';
    const captchaService = createCaptchaService({
        sitekey,
    });

    // other services
    const calendarService = createCalendarService({
        getCurrentEnterpriseIanaTimeZoneIdentifier: () => getCurrentEnterpriseIanaTimeZoneIdentifier(store.getState()),
        getCurrentDateTimeLocalizationInfo: () => getCurrentDateTimeLocalizationInfo(store.getState()),
        appClockService,
    });
    const timeService = createTimeService({
        getCurrentDateTimeLocalizationInfo: () => getCurrentDateTimeLocalizationInfo(store.getState()),
    });

    setAppConfiguration({
        ...appConfiguration.configuration,
        version: __VERSION__,
        nowUtc: null,
    });

    // providers
    const CommanderContextProvider = commanderContextProviderFactory({
        calendarService,
        timeService,
        getDateTimeLocalizationInfo: () => getCurrentDateTimeLocalizationInfo(store.getState()),
    });

    const localizationHandlers = createLocalizationHandlers({
        appLocalizationComponent: container.appLocalizationComponent,
    });
    const LocalizationProvider = createLocalizationProvider();

    const FeatureFlagServiceProvider = createFeatureFlagServiceProvider(featureFlagService);

    const LoggerServiceProvider = createLoggerServiceProvider(loggerService);

    const AppClockServiceProvider = createAppClockServiceProvider(appClockService);

    const RouteResolveProvider = createRouteResolveProvider({ resolveRoute });

    const RepositoryRegistryProvider = createRepositoryRegistryProvider(repositories);
    const GraphqlClientConfigProvider = createGraphqlClientConfigContextProvider({
        commonRepositoryParams: () => getCommonParams(),
    });

    // some init actions already access the app instance, it has to be set first
    setAppInstance({
        isRunning,
        container,
        store,
        resolveRoute,
        featureFlagService,
        IntlLocalizationProvider,
        AppClockServiceProvider,
        CommanderContextProvider,
        // @ts-expect-error 'unknown' is not assignable to LocalizationProviderProps
        LocalizationProvider,
        localizationHandlers,
        FeatureFlagServiceProvider,
        LoggerServiceProvider,
        RouteResolveProvider,
        RepositoryRegistryProvider,
        reactQueryClient,
        gainsightService,
        pciProxyService,
        GraphqlClientConfigProvider,
        captchaService,
    });

    const initCookieBanner = Promise.resolve().then(() => {
        store.dispatch(prepareTrackingConsents());
        store.dispatch(prepareServicesRequiringConsents());
    });

    // scroll restoration
    window.addEventListener('popstate', () => {
        setShouldRestoreScroll(true);
    });

    tanStackRouter.history.subscribe(() => {
        window.setTimeout(() => {
            setNavigatedBack(false);
        });

        navigationOccurred({
            dispatch: store.dispatch,
            gainsightService,
            url: tanStackRouter.history.location.href,
        });

        const routeInfo = getRouteInfo(tanStackRouter.history.location.href);
        if (routeInfo && featureFlagService.hasFlag(FeatureFlag.newRelicCustomAttributes)) {
            void setNewRelicCustomAttributes(store.getState(), {
                ...routeInfo,
                url: tanStackRouter.history.location.href,
            });
        }
    });

    const parseEnterpriseId = Promise.resolve().then(() => {
        try {
            const matches = tanStackRouter.matchRoutes(
                tanStackRouter.state.resolvedLocation.pathname,
                tanStackRouter.state.resolvedLocation.search,
                { throwOnError: true }
            );
            const lastMatch = matches.at(-1);
            if (!lastMatch) {
                return;
            }
            const { currentEnterpriseId } = lastMatch.params;

            /**
             * Platform admin is using a fake GUID as an `currentEnterpriseId` in URLs,
             * however, for the application purposes it needs to be set to `null`.
             */
            if (currentEnterpriseId) {
                updateCurrentSession({
                    currentEnterpriseId:
                        currentEnterpriseId === PLATFORM_ADMIN_ENTERPRISE_ID ? null : currentEnterpriseId,
                });
            }
        } catch (e) {
            return null;
        }
    });

    if (!__LOCALHOST__) {
        checkHostname(appConfiguration.configuration.platformPrimaryUrl);
    }

    const { dispatch } = store;
    const appInitChain = dispatch(
        initApp([
            // prettier-keep-line
            initCookieBanner,
            loadIconFont(),
            dispatch(fetchGlobalization()),
            parseEnterpriseId,
            fullviewService.initialize(),
        ])
    )
        .then(async () =>
            dispatch(tryAutomaticSignIn()).catch(async () =>
                Promise.all([dispatch(fetchCultureInfos()), dispatch(fetchLocalizedTexts())])
            )
        )
        .then(() => {
            void renderApp(appConfiguration.elementId)({
                getState: store.getState,
                ensureQueryData,
                router: tanStackRouter,
            });

            onInitCompleted();

            dispatch(scheduleAppCleanup());

            const currentEnterpriseId = getCurrentEnterpriseId();

            if (currentEnterpriseId) {
                dispatch(setSentryTags({ enterpriseId: currentEnterpriseId }));
            }

            if (performanceMonitoringService) {
                performanceMonitoringService.mesuareAppInitializationTime();
            }
        });

    setAppInitChain(appInitChain);

    // Log additional data
    if (flagServicePromise.status === 'rejected') {
        loggerService.log('featureFlagsService', 'Initialization failed.', {
            message: flagServicePromise.reason.message,
        });
    }
}
